import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  useToast,
  Badge,
  HStack,
  Text,
  Switch,
  VStack,
  Select,
  Checkbox,
  TableContainer,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';
import axios from 'axios';
import ConfirmModal from '../components/ConformModel/ConfirmModal';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [coinPackages, setCoinPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const adminUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const canManageCoupons = useMemo(() => {
    const allowedEmails = ["admin@arrowcoders.com", "haravindh@arrowcoders.com"];
    return allowedEmails.includes(adminUser?.email?.toLowerCase());
  }, [adminUser]);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    category: '',
    discountType: 'percentage', // 'percentage' or 'fixed'
    discountValue: '',
    minPurchase: '',
    maxPurchase: '',
    maxDiscount: '',
    expiryDate: '',
    usageLimit: '',
    usability: 'multiple', // 'one-time' | 'multiple'
    usageLimitType: 'unlimited', // 'count' | 'unlimited' (used when usability === 'multiple')
    active: true,
    platforms: {
      android: true,
      ios: true,
      web: true,
    },
    selectedPackages: [], // Array of selected coin package IDs
  });

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/v2/admin/coupons');
      if (res.data.success) {
        const list = res.data.data || [];
        setCoupons(list);
        // Best-effort categories list derived from existing coupons (supports string or {name})
        const extracted = list
          .map((c) => {
            const cat = c?.category;
            if (!cat) return null;
            if (typeof cat === 'string') return cat;
            if (typeof cat === 'object' && typeof cat.name === 'string') return cat.name;
            return null;
          })
          .filter(Boolean);
        setCategories(Array.from(new Set(extracted)));
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      // Fallback to empty array if API fails
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchCoinPackages = async () => {
  try {
    const res = await axios.get('/api/v1/app/coin-packages');

    const packages = res.data.data?.packages || [];

    console.log("All Packages:", packages);

    const activePackages = packages.filter(pkg => pkg.isActive === true);

    console.log("Filtered Packages:", activePackages);

    setCoinPackages(activePackages);
  } catch (err) {
    console.log(err);
  }
};


  useEffect(() => {
    fetchCoupons();
    fetchCoinPackages();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, active: e.target.checked });
  };

  const handlePlatformChange = (platform, checked) => {
    setFormData({
      ...formData,
      platforms: {
        ...formData.platforms,
        [platform]: checked,
      },
    });
  };


  const handleUsabilityChange = (value) => {
    setFormData((prev) => {
      if (value === 'one-time') {
        return {
          ...prev,
          usability: 'one-time',
          usageLimitType: 'count',
          usageLimit: 1,
        };
      }
      // switching back to multiple
      const nextUsageLimitType =
        prev.usageLimitType === 'count' && prev.usageLimit !== '' && prev.usageLimit !== null
          ? 'count'
          : 'unlimited';
      const nextUsageLimit = nextUsageLimitType === 'count' ? prev.usageLimit : '';
      return {
        ...prev,
        usability: 'multiple',
        usageLimitType: nextUsageLimitType,
        usageLimit: nextUsageLimit,
      };
    });
  };

  const handleUsageLimitTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      usageLimitType: value,
      usageLimit: value === 'unlimited' ? '' : prev.usageLimit,
    }));
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    setCategories((prev) => {
      const exists = prev.some((c) => c.toLowerCase() === name.toLowerCase());
      return exists ? prev : [...prev, name].sort((a, b) => a.localeCompare(b));
    });

    setFormData((prev) => ({ ...prev, category: name }));
    setNewCategoryName('');
    setIsCreatingCategory(false);
  };

  const handleDeleteCategory = (categoryName) => {
    if (!categoryName) return;
    setCategories((prev) => prev.filter((c) => c !== categoryName));
    if (formData.category === categoryName) {
      setFormData((prev) => ({ ...prev, category: '' }));
    }
    setCategoryToDelete('');
    setIsDeletingCategory(false);
    toast({ title: 'Category removed', status: 'info', duration: 2000 });
  };

  const handlePackageSelectionChange = (packageId, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedPackages: checked
        ? [...prev.selectedPackages, packageId]
        : prev.selectedPackages.filter(id => id !== packageId)
    }));
  };

  const handleAddCoupon = () => {
    setEditingCoupon(null);
    setIsCreatingCategory(false);
    setIsDeletingCategory(false);
    setNewCategoryName('');
    setCategoryToDelete('');
    setFormData({
      code: '',
      description: '',
      category: '',
      discountType: 'percentage',
      discountValue: '',
      minPurchase: '',
      maxPurchase: '',
      maxDiscount: '',
      expiryDate: '',
      usageLimit: '',
      usability: 'multiple',
      usageLimitType: 'unlimited',
      active: true,
      platforms: {
        android: true,
        ios: true,
        web: true,
      },
      selectedPackages: [],
    });
    onOpen();
  };



  const handleEditCoupon = (coupon) => {
  setEditingCoupon(coupon);
  setIsCreatingCategory(false);
  setIsDeletingCategory(false);
  setNewCategoryName('');
  setCategoryToDelete('');

  console.log("EDIT COUPON RAW DATA:", coupon);

  const categoryValue =
    typeof coupon.category === 'string'
      ? coupon.category
      : typeof coupon.category?.name === 'string'
        ? coupon.category.name
        : '';

  // ✅ FIX usage limit type
  const usageLimit =
    coupon.usageLimit === null || coupon.usageLimit === undefined
      ? ''
      : coupon.usageLimit;

  const inferredUsageLimitType =
    !usageLimit || usageLimit === 0 ? 'unlimited' : 'count';

  // ✅ FIX selectedPackages (ObjectId → string)
  const selectedPackages = (coupon.selectedPackages || []).map(id =>
    id.toString()
  );

  // ✅ FIX platforms fallback
  const platforms = coupon.platforms || {
    android: true,
    ios: true,
    web: true,
  };

  // ✅ FIX date format
  const expiryDate = coupon.expiryDate
    ? new Date(coupon.expiryDate).toISOString().split('T')[0]
    : '';

  // ✅ FIX numeric fields (convert to string for inputs)
  setFormData({
    code: coupon.code || '',
    description: coupon.description || '',
    category: categoryValue,
    discountType: coupon.discountType || 'percentage',
    discountValue: coupon.discountValue?.toString() || '',
    minPurchase: coupon.minPurchase?.toString() || '',
    maxPurchase: coupon.maxPurchase?.toString() || '',
    maxDiscount: coupon.maxDiscount?.toString() || '',
    expiryDate,
    usability: coupon.usability || 'multiple',
    usageLimitType: coupon.usability === 'one-time' ? 'count' : inferredUsageLimitType,
    usageLimit:
      coupon.usability === 'one-time'
        ? 1
        : inferredUsageLimitType === 'count'
          ? usageLimit.toString()
          : '',
    active: coupon.active ?? true,
    platforms,
    selectedPackages,
  });

  onOpen();
};



  const handleSubmit = async () => {
    try {
      const normalizedUsageLimit =
        formData.usability === 'one-time'
          ? 1
          : formData.usageLimitType === 'count'
            ? Number(formData.usageLimit || 0)
            : 0;

      const payload = {
        ...formData,
        usageLimit: normalizedUsageLimit,
        minPurchase: Number(formData.minPurchase || 0),
        maxPurchase: Number(formData.maxPurchase || 0),
        maxDiscount: Number(formData.maxDiscount || 0),
        discountValue: Number(formData.discountValue || 0),
        selectedPackages: formData.selectedPackages,
      };

      console.log("Sending Coupon Payload:", payload);

      if (editingCoupon) {
        const res = await axios.put(`/api/v2/admin/coupons/${editingCoupon._id}`, payload);
        if (res.data.success) {
          toast({ title: 'Coupon updated', status: 'success', duration: 3000 });
        }
      } else {
        const res = await axios.post('/api/v2/admin/coupons', payload);
        if (res.data.success) {
          toast({ title: 'Coupon created', status: 'success', duration: 3000 });
        }
      }

      fetchCoupons();
      onClose();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast({ title: 'Error saving coupon', status: 'error', duration: 3000 });
    }
  };



  const handleDeleteClick = (coupon) => {
    setCouponToDelete(coupon);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await axios.delete(`/api/v2/admin/coupons/${couponToDelete._id}`);
      if (res.data.success) {
        toast({ title: 'Coupon deleted', status: 'success', duration: 3000 });
        fetchCoupons();
      }
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast({ title: 'Error deleting coupon', status: 'error', duration: 3000 });
    } finally {
      setDeleteConfirmOpen(false);
      setCouponToDelete(null);
    }
  };

  return (
    <Box p={6} bg="white" minH="screen">
      <VStack align="stretch" spacing={6}>
        <Box
          display="flex"
          justifyContent="between"
          alignItems="center"
          bg="white"
          p={6}
          rounded="xl"
          border="2px"
          borderColor="gray.200"
          shadow="sm"
        >
          <Box>
            <HStack spacing={3}>
              <FiTag size={24} color="var(--primary-solid)" />
              <Text fontSize="2xl" fontWeight="bold" color="var(--primary-solid)">Coupon Management</Text>
            </HStack>
            <Text color="gray.500" mt={1}>Create and manage discount coupons for users</Text>
          </Box>
          {canManageCoupons && (
            <Button
              leftIcon={<FiPlus />}
              onClick={handleAddCoupon}
              bg="var(--primary-solid)"
              color="white"
              _hover={{ opacity: 0.9 }}
              ml="auto"
            >
              Add New Coupon
            </Button>
          )}
        </Box>

        <Box bg="white" rounded="xl" border="2px" borderColor="gray.200" shadow="sm" overflow="hidden">
         <TableContainer overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Code</Th>
                <Th>Category</Th>
                <Th>Discount</Th>
                <Th>Min Purchase</Th>
                <Th>Expiry</Th>
                <Th>Limit</Th>
                <Th>Applicable Packages</Th>
                <Th>Status</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {coupons.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={10}>
                    {loading ? 'Loading coupons...' : 'No coupons found'}
                  </Td>
                </Tr>
              ) : (
                coupons.map((coupon) => {
                  const applicablePackages = coupon.selectedPackages || [];
                  const applicablePackageNames = applicablePackages.length > 0
                    ? coinPackages
                      .filter(pkg => applicablePackages.some(id => id.toString() === pkg._id.toString()))
                      .map(pkg => pkg.name)
                      .join(', ')
                    : 'All Packages';

                  return (
                    <Tr key={coupon._id} _hover={{ bg: 'gray.50' }}>
                      <Td fontWeight="bold">{coupon.code}</Td>
                      <Td>
                        {typeof coupon.category === 'string'
                          ? coupon.category
                          : typeof coupon.category?.name === 'string'
                            ? coupon.category.name
                            : '—'}
                      </Td>
                      <Td>
                        {coupon.discountType === 'percentage'
                          ? `${coupon.discountValue}%`
                          : `₹${coupon.discountValue}`}
                      </Td>
                      <Td>₹{coupon.minPurchase || '0'}</Td>
                      <Td>{coupon.expiryDate ? new Date(coupon.expiryDate).toLocaleDateString() : 'No expiry'}</Td>
                      <Td>{coupon.usageLimit || 'Unlimited'}</Td>
                      <Td>
                        <Text fontSize="sm" title={applicablePackageNames}>
                          {applicablePackageNames.length > 20
                            ? `${applicablePackageNames.substring(0, 20)}...`
                            : applicablePackageNames}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={coupon.active ? 'green' : 'red'} rounded="full" px={3}>
                          {coupon.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td textAlign="right">
                        {canManageCoupons && (
                          <HStack spacing={2} justify="end">
                            <IconButton
                              aria-label="Edit"
                              icon={<FiEdit2 />}
                              size="sm"
                              variant="ghost"
                              color="blue.500"
                              onClick={() => handleEditCoupon(coupon)}
                            />
                            <IconButton
                              aria-label="Delete"
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              color="red.500"
                              onClick={() => handleDeleteClick(coupon)}
                            />
                          </HStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
          </TableContainer>
        </Box>
      </VStack>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="2xl">
          <ModalHeader borderBottom="1px" borderColor="gray.100">
            {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Coupon Code</FormLabel>
                <Input
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g. WELCOME50"
                  rounded="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional description for internal reference"
                  rounded="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Category</FormLabel>
                <Select
                  value={isCreatingCategory ? '__create__' : isDeletingCategory ? '__delete__' : (formData.category || '')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__create__') {
                      setIsCreatingCategory(true);
                      setIsDeletingCategory(false);
                      setCategoryToDelete('');
                      return;
                    }
                    if (v === '__delete__') {
                      setIsDeletingCategory(true);
                      setIsCreatingCategory(false);
                      setFormData((prev) => ({ ...prev, category: '' }));
                      return;
                    }
                    setIsCreatingCategory(false);
                    setIsDeletingCategory(false);
                    setCategoryToDelete('');
                    setFormData((prev) => ({ ...prev, category: v }));
                  }}
                  rounded="lg"
                >
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__create__">+ Create new category</option>
                  {categories.length > 0 && (
                    <option value="__delete__">— Delete a category —</option>
                  )}
                </Select>

                {isCreatingCategory && (
                  <HStack mt={2}>
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      rounded="lg"
                    />
                    <Button onClick={handleAddCategory} bg="var(--primary-solid)" color="white" _hover={{ opacity: 0.9 }}>
                      Add
                    </Button>
                  </HStack>
                )}

                {isDeletingCategory && categories.length > 0 && (
                  <HStack mt={2} spacing={2} alignItems="flex-end">
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">Category to delete</FormLabel>
                      <Select
                        value={categoryToDelete}
                        onChange={(e) => setCategoryToDelete(e.target.value)}
                        placeholder="Choose category"
                        rounded="lg"
                      >
                        {categories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      leftIcon={<FiTrash2 />}
                      colorScheme="red"
                      variant="outline"
                      isDisabled={!categoryToDelete}
                      onClick={() => handleDeleteCategory(categoryToDelete)}
                    >
                      Delete
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setIsDeletingCategory(false); setCategoryToDelete(''); }}>
                      Cancel
                    </Button>
                  </HStack>
                )}
              </FormControl>

              <HStack w="full" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">Discount Type</FormLabel>
                  <Select name="discountType" value={formData.discountType} onChange={handleInputChange} rounded="lg">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">Discount Value</FormLabel>
                  <Input
                    name="discountValue"
                    type="number"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    placeholder="Value"
                    rounded="lg"
                  />
                </FormControl>
              </HStack>

              <HStack w="full" spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="bold">Min Purchase (₹)</FormLabel>
                  <Input
                    name="minPurchase"
                    type="number"
                    value={formData.minPurchase}
                    onChange={handleInputChange}
                    placeholder="Minimum amount"
                    rounded="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Max Purchase (₹)</FormLabel>
                  <Input
                    name="maxPurchase"
                    type="number"
                    value={formData.maxPurchase}
                    onChange={handleInputChange}
                    placeholder="Maximum amount"
                    rounded="lg"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontWeight="bold">Max Discount (₹)</FormLabel>
                <Input
                  name="maxDiscount"
                  type="number"
                  value={formData.maxDiscount}
                  onChange={handleInputChange}
                  placeholder="Maximum discount"
                  rounded="lg"
                />
              </FormControl>

              <HStack w="full" spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="bold">Expiry Date</FormLabel>
                  <Input
                    name="expiryDate"
                    type="date"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    rounded="lg"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold">Usability</FormLabel>
                  <Select
                    value={formData.usability}
                    onChange={(e) => handleUsabilityChange(e.target.value)}
                    rounded="lg"
                  >
                    <option value="one-time">One time</option>
                    <option value="multiple">Multiple times</option>
                  </Select>
                </FormControl>
              </HStack>

              {formData.usability === 'multiple' && (
                <HStack w="full" spacing={4}>
                  <FormControl>
                    <FormLabel fontWeight="bold">Usage Count</FormLabel>
                    <Select
                      value={formData.usageLimitType}
                      onChange={(e) => handleUsageLimitTypeChange(e.target.value)}
                      rounded="lg"
                    >
                      <option value="unlimited">Unlimited</option>
                      <option value="count">Set count</option>
                    </Select>
                  </FormControl>
                  <FormControl isDisabled={formData.usageLimitType !== 'count'}>
                    <FormLabel fontWeight="bold">Count</FormLabel>
                    <Input
                      name="usageLimit"
                      type="number"
                      min={1}
                      value={formData.usageLimitType === 'count' ? formData.usageLimit : ''}
                      onChange={handleInputChange}
                      placeholder="e.g. 100"
                      rounded="lg"
                    />
                  </FormControl>
                </HStack>
              )}

              <HStack spacing="8" alignItems="flex-start">
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0" fontWeight="bold">Active Status</FormLabel>
                  <Switch
                    isChecked={formData.active}
                    onChange={handleSwitchChange}
                    colorScheme="pink"
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel fontWeight="bold" mb="0" mr="4">Platform Availability</FormLabel>
                  <HStack spacing="6">
                    <Checkbox
                      isChecked={formData.platforms.android}
                      onChange={(e) => handlePlatformChange('android', e.target.checked)}
                      colorScheme="pink"
                    >
                      Android
                    </Checkbox>
                    <Checkbox
                      isChecked={formData.platforms.ios}
                      onChange={(e) => handlePlatformChange('ios', e.target.checked)}
                      colorScheme="pink"
                    >
                      iOS
                    </Checkbox>
                    <Checkbox
                      isChecked={formData.platforms.web}
                      onChange={(e) => handlePlatformChange('web', e.target.checked)}
                      colorScheme="pink"
                    >
                      web
                    </Checkbox>
                  </HStack>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontWeight="bold">Applicable Coin Packages</FormLabel>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Select the coin packages this coupon applies to. Leave empty to apply to all packages.
                </Text>
                <Box maxH="200px" overflowY="auto" border="1px" borderColor="gray.200" rounded="md" p={2}>
                  {coinPackages.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">Loading coin packages...</Text>
                  ) : (
                    <VStack align="start" spacing={2}>
                      {coinPackages.map((pkg) => (
                        <Checkbox
                          key={pkg._id || pkg.id}
                          isChecked={formData.selectedPackages.includes(pkg._id || pkg.id)}
                          onChange={(e) => handlePackageSelectionChange(pkg._id || pkg.id, e.target.checked)}
                          colorScheme="pink"
                        >
                          <HStack>
                            <Text fontSize="sm" fontWeight="medium">{pkg.name}</Text>
                            <Text fontSize="xs" color="gray.500">({pkg.coins} coins)</Text>
                          </HStack>
                        </Checkbox>
                      ))}
                    </VStack>
                  )}
                </Box>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor="gray.100">
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button
              bg="var(--primary-solid)"
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={handleSubmit}
            >
              {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        title="Delete Coupon"
        message={`Are you sure you want to delete coupon? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmOpen(false)}
        block="Delete"
      />
    </Box>
  );
};

export default Coupons;
